import { useMemo, useState } from 'react'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deleteNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../shared/api/notifications.api'
import type { NotificationDto, NotificationType } from '../../shared/api/types'
import {
  formatNotificationDate,
  getNotificationMeta,
  getNotificationRoute,
  NOTIFICATION_TYPE_LABELS,
} from './notificationUtils'

type NotificationMeta = {
  clientCode?: string | null
  requestNumber?: string | null
  partNumber?: string | null
  commentText?: string | null
}

function MetaLabels({ n }: { n: NotificationDto }) {
  const raw = getNotificationMeta(n) as NotificationMeta
  const clientCode = raw.clientCode
  const requestNumber = raw.requestNumber
  const partNumber = raw.partNumber
  const commentText = raw.commentText

  if (!clientCode && !requestNumber && !partNumber && !commentText) return null

  return (
    <div className="space-y-0.5">
      {(clientCode || requestNumber) && (
        <div>
          {clientCode && <span className="font-medium">{clientCode}</span>}
          {clientCode && requestNumber && <span className="text-gray-400"> · </span>}
          {requestNumber && <span>{requestNumber}</span>}
        </div>
      )}
      {partNumber && (
        <div>
          Позиция: <span className="font-medium">{partNumber}</span>
        </div>
      )}
      {commentText && (
        <div className="text-gray-400 italic line-clamp-2">
          «{commentText}
          {commentText.length >= 80 ? '…' : ''}»
        </div>
      )}
    </div>
  )
}

export function NotificationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [onlyUnread, setOnlyUnread] = useState(false)
  const [type, setType] = useState<NotificationType | ''>('')

  const filters = useMemo(
    () => ({ isRead: onlyUnread ? false : undefined, type: type || undefined }),
    [onlyUnread, type],
  )

  // Бесконечная лента (03.08): раньше грузилась ровно одна страница из 50 —
  // всё старше было недостижимо («история обрывается»). Теперь offset-страницы
  // по кнопке «Показать ещё» + дедуп по id (прочтение меняет сортировку
  // isRead-first, и границы страниц могут наехать друг на друга).
  const PAGE_SIZE = 50
  const { data, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ['notifications', filters],
      queryFn: ({ pageParam }) =>
        getNotifications({ ...filters, page: pageParam, limit: PAGE_SIZE }),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.page * last.limit < last.total ? last.page + 1 : undefined),
    })

  const items = useMemo(() => {
    const seen = new Set<string>()
    return (data?.pages ?? [])
      .flatMap((p) => p.items)
      .filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)))
  }, [data])
  const lastPage = data?.pages.at(-1)
  // Выпадашка типов — из РЕАЛЬНЫХ типов уведомлений пользователя (04.08):
  // раньше список был статичным — половина пунктов у конкретной роли была
  // мёртвой (её типы не приходят), а живые ORDER_*/упоминания отсутствовали.
  const typeOptions = useMemo(() => {
    const available = new Set(lastPage?.availableTypes ?? [])
    if (type) available.add(type) // выбранный не выкидываем из списка
    return [
      { value: '' as const, label: 'Все типы' },
      ...[...available]
        .map((t) => ({ value: t, label: NOTIFICATION_TYPE_LABELS[t] ?? t }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    ]
  }, [lastPage?.availableTypes, type])
  // При активном фильтре типа шапка и кнопка работают по счётчику ЭТОГО типа —
  // иначе «Непрочитанных: 7 · Всего по фильтру: 3» выглядело бы враньём, а
  // кнопка кликалась бы в no-op (ревью 03.08).
  const unreadCount = (type ? lastPage?.unreadCountFiltered : lastPage?.unreadCount) ?? 0
  const total = lastPage?.total ?? 0

  const markReadMut = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markAllMut = useMutation({
    mutationFn: () => markAllNotificationsRead(type || undefined),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const deleteMut = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const openNotification = (notification: NotificationDto) => {
    if (!notification.isRead) markReadMut.mutate(notification.id)
    navigate(getNotificationRoute(notification))
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Уведомления</h1>
          <p className="text-sm text-gray-500">
            Непрочитанных: {unreadCount} · Всего{type || onlyUnread ? ' по фильтру' : ''}: {total}
          </p>
        </div>
        <button
          type="button"
          onClick={() => markAllMut.mutate()}
          disabled={unreadCount === 0 || markAllMut.isPending}
          title={
            type
              ? 'Пометить прочитанными только уведомления выбранного типа'
              : 'Пометить прочитанными все уведомления'
          }
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm text-white hover:bg-gray-800 disabled:opacity-40"
        >
          {type ? 'Прочитать все этого типа' : 'Прочитать все'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-600"
          />
          Только непрочитанные
        </label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as NotificationType | '')}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm"
        >
          {typeOptions.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Загрузка...</div>
        ) : isError ? (
          // Иначе сбой загрузки выглядит как «Уведомлений нет».
          <div className="p-4">
            <ErrorMessage error={error} />
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Уведомлений нет</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((n) => (
              <div
                key={n.id}
                className={`group flex items-start gap-2 px-4 py-3 transition-colors hover:bg-gray-50 ${
                  n.isRead ? 'bg-white' : 'bg-brand-50/60'
                }`}
              >
                <button
                  type="button"
                  onClick={() => openNotification(n)}
                  className="grid min-w-0 flex-1 gap-2 text-left md:grid-cols-[180px_1fr_220px]"
                >
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-700">
                      {NOTIFICATION_TYPE_LABELS[n.type]}
                    </div>
                    <div className="text-xs text-gray-400">
                      {formatNotificationDate(n.createdAt)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{n.title}</div>
                    <div className="mt-0.5 text-sm text-gray-600">{n.message}</div>
                    {n.actor && (
                      <div className="mt-1 text-xs text-gray-400">
                        {n.actor.name ?? n.actor.email}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    <MetaLabels n={n} />
                  </div>
                </button>
                <button
                  type="button"
                  title="Удалить уведомление"
                  disabled={deleteMut.isPending}
                  onClick={() => deleteMut.mutate(n.id)}
                  className="mt-0.5 shrink-0 rounded p-1 text-gray-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 disabled:opacity-30"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {hasNextPage && (
          <div className="border-t border-gray-100 px-4 py-3 text-center">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Загружаю…' : `Показать ещё (${items.length} из ${total})`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
